import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import * as Yup from "yup";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import TextInput from "../inputs/TextInput";
import { Controller } from "react-hook-form";
import { Button } from "../button";
import { useGetSearchedUserDetails } from "../../hooks/visitor/mutation";
import useAuthStore from "../../store/Auth";
import toast from "react-hot-toast";
import i18n from "../../i18n";

interface SearchExistingVisitorInfoCardProps {
  setExistingVisitorAppointment: Dispatch<SetStateAction<undefined>>;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

const SearchExistingVisitorInfoCardSchema = Yup.object({
  eid: Yup.string().matches(
    /^$|\d{1,10}$/,
    i18n.language === "en"
      ? "Enter a valid National ID."
      : "أدخل رقم هوية وطنية صالحاً."
  ),
  passportNumber: Yup.string(),
  iqama: Yup.string().matches(
    /^$|\d{1,10}$/,
    i18n.language === "en"
      ? "Enter a valid iqama number."
      : "أدخل رقم إقامة صالح."
  ),
  visaNumber: Yup.string().matches(
    /^[A-Z0-9][A-Z0-9\s-]{5,13}$/,
    i18n.language === "en"
      ? "Enter a valid visa number"
      : "أدخل رقم تأشيرة صالحًا"
  ),
  email: Yup.string().email(
    i18n.language === "en"
      ? "Enter valid email format."
      : "أدخل تنسيق بريد إلكتروني صالح"
  ),
  number: Yup.string().matches(
    /^$|\d{1,14}$/,
    i18n.language === "en" ? "Enter a valid number." : "أدخل رقماً صحيحاً."
  ),
});

const initialState = {
  eid: "",
  passportNumber: "",
  iqama: "",
  email: "",
  number: "",
  visaNumber: "",
};

interface SearchExistingVisitorInfoData {
  eid?: string;
  passportNumber?: string;
  iqama?: string;
  email?: string;
  visaNumber?: string;
  number?: string;
}

const SearchExistingVisitorInfoCard = ({
  setExistingVisitorAppointment,
  setIsLoading,
}: SearchExistingVisitorInfoCardProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useFormWithSchema(SearchExistingVisitorInfoCardSchema);

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const user = useAuthStore((state) => state.user);

  const {
    mutateAsync: GetSearchedUserDetails,
    isPending: isGetSearchedUserDetailsLoading,
  } = useGetSearchedUserDetails();

  useEffect(() => {
    setIsLoading?.(isGetSearchedUserDetailsLoading);
  }, [isGetSearchedUserDetailsLoading, setIsLoading]);

  const onSubmit = useCallback(
    async (data: SearchExistingVisitorInfoData) => {
      try {
        if (
          data.email ||
          data.iqama ||
          data.eid ||
          data.number ||
          data.passportNumber ||
          data.visaNumber
        ) {
          const body = {
            N_UserId: user?.user_id,
            N_HostID: user?.user_id,
            ...(data.eid && { S_Id: data.eid }),
            ...(data.passportNumber && {
              S_PassportNumber: data.passportNumber,
            }),
            ...(data.iqama && { S_Iqama: data.iqama }),
            ...(data.visaNumber && { S_VisaNo: data.visaNumber }),
            S_Email: data.email,
            S_Number: data.number,
          };

          const res = await GetSearchedUserDetails(body);

          if (res?.appointment) {
            setExistingVisitorAppointment(res);
          } else {
            toast.error(t("visitorDoesNotExist"));
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [GetSearchedUserDetails, setExistingVisitorAppointment, user?.user_id, t]
  );

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchExistingVisitorInformation")}
          </h1>
        }
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
            <Controller
              name="iqama"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={t("iqamaNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterIqamaNumber")}</p>}
                  />
                </div>
              )}
            />
            <Controller
              name="eid"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={t("nationalID")}
                    onChange={onChange}
                    value={value}
                    error={errors?.[name]?.message}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterNationalId")}</p>}
                  />
                </div>
              )}
            />
            <Controller
              name="passportNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={t("passportNumber")}
                    value={value}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="visaNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={t("visaNumber")}
                    value={value}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={t("email")}
                    onChange={onChange}
                    value={value}
                    error={errors?.[name]?.message}
                  />
                </div>
              )}
            />
            <Controller
              name="number"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={t("phoneNumber")}
                    onChange={onChange}
                    value={value}
                    error={errors?.[name]?.message}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                  />
                </div>
              )}
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              isLoading={isGetSearchedUserDetailsLoading}
              className="px-4 sm:px-10 sm:max-w-max"
            >
              {t("search")}
            </Button>
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent px-4 sm:px-10 sm:max-w-max ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </form>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchExistingVisitorInfoCard);
